import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Address,
  AddressRepository,
  CartItem,
  CartRepository,
  Loading,
} from "../domain";
import { Endpoints, HttpClient } from "../infrastructure";
import { ServiceLocater, Services } from "../service-locater";
import { AppThunk } from "./store";

type DeliveryInfo = {
  deliveryPrice: number;
  freeDeliveryMinPrice: number;
};

type ErrorCodes =
  | "wrongDiscountCode"
  | "expiredDiscountCode"
  | "codeAlreadyUsed"
  | "invalidCart";

type ShippingState = {
  loading: Loading;
  paymentLinkLoading: Loading;
  applyOffLoading: Loading;
  address?: Address;
  isAddressValid?: boolean;
  deliveryInfo?: DeliveryInfo;
  errorCode?: ErrorCodes;
  discountOffAmount: number;
  discountOffType?: "product" | "delivery";
};

const initialState: ShippingState = {
  loading: "idle",
  paymentLinkLoading: "idle",
  applyOffLoading: "idle",
  discountOffAmount: 0,
};

const shippingSlice = createSlice({
  name: "shippingSlice",
  initialState,
  reducers: {
    shippingLoading(state) {
      state.loading = "pending";
    },
    shippingSucceded(
      state,
      {
        payload,
      }: PayloadAction<{ address: Address; deliveryInfo: DeliveryInfo }>
    ) {
      state.address = payload.address;
      state.deliveryInfo = payload.deliveryInfo;
      state.loading = "succeeded";
    },
    shippingFailed(state, { payload }: PayloadAction<boolean>) {
      state.isAddressValid = payload;
      state.loading = "failed";
    },
    paymentLinkLoading(state) {
      state.errorCode = undefined;
      state.paymentLinkLoading = "pending";
    },
    paymentLinkFailed(
      state,
      { payload }: PayloadAction<ErrorCodes | undefined>
    ) {
      state.errorCode = payload;
      state.paymentLinkLoading = "failed";
      state.applyOffLoading = "failed";
    },
    resetShippingState(state) {
      state.address = undefined;
      state.deliveryInfo = undefined;
      state.errorCode = undefined;
      state.isAddressValid = undefined;
      state.loading = "idle";
      state.paymentLinkLoading = "idle";
    },
    setDiscountOffAmount(state, { payload }) {
      state.discountOffAmount = payload.amount;
      state.discountOffType = payload.type;
      state.applyOffLoading = "succeeded";
    },
    applyOffLoading(state) {
      state.discountOffAmount = 0;
      state.discountOffType = undefined;
      state.applyOffLoading = "pending";
      state.errorCode = undefined;
    },
  },
});

export default shippingSlice.reducer;
export const { resetShippingState } = shippingSlice.actions;

const {
  paymentLinkFailed,
  paymentLinkLoading,
  shippingFailed,
  shippingLoading,
  shippingSucceded,
  setDiscountOffAmount,
  applyOffLoading,
} = shippingSlice.actions;

export const loadShipping = (addressId: string): AppThunk => async (
  dispatch
) => {
  try {
    dispatch(shippingLoading());
    const client = ServiceLocater.get<HttpClient>(Services.HttpClinet);
    const deliveryInfo = await client.get(Endpoints.deliveryInfo);
    const addresses = await ServiceLocater.get<AddressRepository>(
      Services.AddressRepository
    ).get();
    addresses.fold(
      (_) => {
        dispatch(shippingFailed(true));
      },
      (addresses) => {
        const address = addresses.find(
          (address) => address.id.toString() === addressId
        );
        if (address === undefined) {
          dispatch(shippingFailed(false));
        } else {
          dispatch(
            shippingSucceded({
              deliveryInfo: {
                deliveryPrice: deliveryInfo.data.delivery_price,
                freeDeliveryMinPrice: deliveryInfo.data.free_delivery_min_price,
              },
              address,
            })
          );
        }
      }
    );
  } catch (error) {
    dispatch(shippingFailed(true));
    if (!error.response) {
    }
  }
};

export const applyOff = (disaountCode: string): AppThunk => async (
  dispatch
) => {
  try {
    dispatch(applyOffLoading());
    const res = await ServiceLocater.get<HttpClient>(
      Services.HttpClinet
    ).post(Endpoints.checkDiscountCode, { discount_code: disaountCode });
    dispatch(
      setDiscountOffAmount({
        amount: res.data.amount,
        type: res.data.type === "product" ? "product" : "delivery",
      })
    );
  } catch (error) {
    if (!error.response) {
      dispatch(paymentLinkFailed());
    } else {
      const errorCode = error.response.data.error_code;
      if (errorCode === 100 || errorCode === 101)
        dispatch(paymentLinkFailed("wrongDiscountCode"));
      else if (errorCode === 102)
        dispatch(paymentLinkFailed("expiredDiscountCode"));
      else if (errorCode === 103) dispatch(paymentLinkFailed("invalidCart"));
      else if (errorCode === 104)
        dispatch(paymentLinkFailed("codeAlreadyUsed"));
      else dispatch(paymentLinkFailed());
    }
  }
};

export const getPaymentLink = (
  addressId: string,
  items: CartItem[],
  disaountCode?: string
): AppThunk => async (dispatch) => {
  try {
    dispatch(paymentLinkLoading());
    const res = await ServiceLocater.get<HttpClient>(Services.HttpClinet).post(
      Endpoints.requestPayment,
      {
        address_id: addressId,
        items: items.map((cartItem) => {
          const { productId, attributeId, count } = cartItem;
          return {
            product_id: productId,
            attribute_id: attributeId,
            count: count,
          };
        }),
        ...(disaountCode === undefined ? {} : { discount_code: disaountCode }),
      }
    );
    ServiceLocater.get<CartRepository>(Services.CartRepository).clearCart();
    window.location.href = res.data.url;
  } catch (error) {
    if (!error.response) {
      dispatch(paymentLinkFailed());
    } else {
      const errorCode = error.response.data.error_code;
      if (errorCode === 100 || errorCode === 101)
        dispatch(paymentLinkFailed("wrongDiscountCode"));
      else if (errorCode === 102)
        dispatch(paymentLinkFailed("expiredDiscountCode"));
      else if (errorCode === 103) dispatch(paymentLinkFailed("invalidCart"));
      else dispatch(paymentLinkFailed());
    }
  }
};
